import { AccordionItem, DismissibleTag } from '@carbon/react';

import {
  SpatialFilterControls,
  type SpatialFilterMode,
} from 'components/query-map';
import { formatNumber } from 'utils/number';

import type { MapFilterMode } from './types';
import styles from './map-query-source.module.scss';

/** The Map Filter modes, in display order. No Map Filter is the default. */
const MAP_FILTER_MODES: SpatialFilterMode<MapFilterMode>[] = [
  { mode: 'none', label: 'No Map Filter' },
  { mode: 'extent', label: 'Use Visible Map Extent' },
  { mode: 'aoi', label: 'Draw AOI Polygon' },
];

export interface MapFilterSectionProps {
  mode: MapFilterMode;
  onModeChange: (mode: MapFilterMode) => void;
  hasPolygon: boolean;
  aoiEditing: boolean;
  onEdit: () => void;
  onDone: () => void;
  onClear: () => void;
  /** Tracts passing the entity and location ticks that have no shape; null with no map filter on. */
  noGeometryCount: number | null;
}

/**
 * The Map Filter section: No Map Filter, Use Visible Map Extent or Draw AOI
 * Polygon. Under Draw AOI Polygon, the "AOI Polygon" row edits (moves the
 * corners, then Done) or clears the polygon (drawing is armed again), and the
 * "Polygon drawn" chip confirms a closed ring. While a map filter is on, a
 * caption says how many Tracts it drops for having no shape.
 *
 * @param {MapFilterSectionProps} props - The mode, the polygon's state and handlers
 * @return {JSX.Element} The accordion section
 */
export function MapFilterSection({
  mode,
  onModeChange,
  hasPolygon,
  aoiEditing,
  onEdit,
  onDone,
  onClear,
  noGeometryCount,
}: MapFilterSectionProps) {
  const aoiNote = (() => {
    if (!hasPolygon) {
      return (
        <p className={styles.caption}>
          Click the map to add vertices; click the first point or double-click
          to finish.
        </p>
      );
    }
    if (aoiEditing) {
      return (
        <p className={styles.caption}>
          Edit vertices on the map, then click Done
        </p>
      );
    }
    return (
      <DismissibleTag
        type="gray"
        size="sm"
        text="Polygon drawn"
        title="Remove polygon filter"
        dismissTooltipLabel="Remove polygon filter"
        onClose={onClear}
        className={styles.polygonChip}
      />
    );
  })();

  return (
    <AccordionItem title="Map Filter" open>
      <SpatialFilterControls
        name="package-map-query-filter"
        idPrefix="package-map-query-filter"
        legendText="Search by"
        modes={MAP_FILTER_MODES}
        mode={mode}
        onModeChange={onModeChange}
        aoi={
          mode === 'aoi'
            ? {
                hasPolygon,
                editLabel: 'Edit polygon',
                deleteLabel: 'Delete polygon',
                onEdit,
                onDelete: onClear,
                dangerDelete: true,
                editing: aoiEditing,
                onDone,
                children: aoiNote,
              }
            : undefined
        }
      />
      {noGeometryCount !== null && noGeometryCount > 0 && (
        <p className={styles.caption}>
          {formatNumber({ value: noGeometryCount, minPrecision: 0 })} tract
          {noGeometryCount === 1 ? '' : 's'} without map geometry{' '}
          {noGeometryCount === 1 ? 'is' : 'are'} excluded while a map filter is
          active. Add them by list if needed.
        </p>
      )}
    </AccordionItem>
  );
}
